import { useEffect, useMemo } from 'react';
import { SidebarItem, useSidebarOpenState } from '@backstage/core-components';
import type { IconElement } from '@backstage/frontend-plugin-api';
import { readLastVisit, recordVisit, useLastVisit } from './lastVisit';
import { useTasksPageShown } from './pagePresence';
import { newestTaskTime, tasksBadge, type TasksBadge } from './tasks';
import { IDLE_BADGE_INTERVAL_MS, listOf, useTaskList } from './useTaskList';
import { RailTooltip } from '../projectsNav/RailTooltip';

/**
 * The accessible name of the entry: its title, then what the badge says —
 * the badge itself is hidden from assistive technology.
 */
export function tasksNavLabel(title: string, badge: TasksBadge): string {
  const parts = [title];
  if (badge.newFailures > 0) {
    parts.push(
      badge.newFailures === 1
        ? '1 échec depuis votre dernière visite'
        : `${badge.newFailures} échecs depuis votre dernière visite`,
    );
  }
  if (badge.running > 0) parts.push(`${badge.running} en cours`);
  return parts.join(', ');
}

/** Red with the new failures, else blue with the running tasks, else none. */
function badgeLook(badge: TasksBadge): {
  tone?: 'danger' | 'info';
  count: number;
} {
  if (badge.newFailures > 0)
    return { tone: 'danger', count: badge.newFailures };
  if (badge.running > 0) return { tone: 'info', count: badge.running };
  return { count: 0 };
}

/**
 * The badge of « Tâches », as the sidebar and the phone's bottom bar draw it:
 *  - blue « ● N », N the running tasks the user can see — their own, every
 *    task for an admin;
 *  - red « ● N » instead, N the failed tasks that ended after the last
 *    visit of the page (`lastVisit.ts`), until the page is shown again;
 *  - nothing when neither.
 *
 * Reads (`useTaskList.ts`): while the « Tâches » page is mounted, none —
 * the counts come from the page's list; otherwise its own small read (the
 * most recent running or failed tasks), every 60 s while the document is
 * visible, every 10 s while a task runs, none while hidden. The reads are
 * shared (`sharedLoad.ts`, `SHARED_TTL_MS`): the sidebar's entry and the
 * bar's icon, both mounted while the phone's menu is open, reuse each
 * other's recent answers rather than each polling on its own.
 *
 * Without any recorded visit, the first list read is taken as one: the
 * failures already there are old news.
 */
function useTasksBadge(): {
  badge: TasksBadge;
  tone?: 'danger' | 'info';
  count: number;
} {
  const pageShown = useTasksPageShown();
  const own = useTaskList({
    query: 'badge',
    idleIntervalMs: IDLE_BADGE_INTERVAL_MS,
    passive: pageShown,
  });
  const page = useTaskList({ query: 'page', passive: true });
  const lastVisit = useLastVisit();

  const list = listOf(pageShown ? page.state : own.state);
  useEffect(() => {
    if (list && readLastVisit() === undefined) {
      recordVisit(newestTaskTime(list.tasks));
    }
  }, [list]);

  const badge = useMemo(
    () => tasksBadge(list?.tasks ?? [], lastVisit),
    [list, lastVisit],
  );
  return { badge, ...badgeLook(badge) };
}

/** The icon with the count over its top right corner, in the badge's tone. */
function IconWithCount(props: {
  icon: IconElement;
  tone?: 'danger' | 'info';
  count: number;
}) {
  const { icon, tone, count } = props;
  return (
    <span className="waadoo-tasks-rail-icon">
      {icon}
      {tone && (
        <span
          className="waadoo-tasks-badge"
          data-tone={tone}
          data-compact=""
          aria-hidden
        >
          {count}
        </span>
      )}
    </span>
  );
}

/**
 * The icon of « Tâches » in the phone's bottom bar (modules/nav/Sidebar.tsx,
 * a `SidebarGroup` icon): the badge's count over it, as in the compact
 * menu. The bar names the entry by the group's label alone (core-components
 * MobileSidebarGroup sets `aria-label={label}`), so the count is not read
 * out there; the « Menu » entry still says it.
 */
export function TasksBarIcon(props: { icon: IconElement }) {
  const { tone, count } = useTasksBadge();
  return <IconWithCount icon={props.icon} tone={tone} count={count} />;
}

/**
 * The sidebar's « Tâches » entry (mockup « M1 », 2026-10-15), with its badge
 * (`useTasksBadge`).
 */
export function TasksNavItem(props: {
  href: string;
  title: string;
  icon: IconElement;
}) {
  const { href, title, icon } = props;
  const { badge, tone, count } = useTasksBadge();
  const { isOpen } = useSidebarOpenState();

  if (!isOpen) {
    // The compact menu (modules/nav/compactNav.ts): the icon alone, the
    // count over its corner in the badge's tone, the name as tooltip.
    const compactIcon = () => (
      <IconWithCount icon={icon} tone={tone} count={count} />
    );
    return (
      <RailTooltip label={title}>
        <SidebarItem
          icon={compactIcon}
          to={href}
          text={title}
          aria-label={tasksNavLabel(title, badge)}
        />
      </RailTooltip>
    );
  }

  return (
    <SidebarItem
      icon={() => icon}
      to={href}
      text={title}
      aria-label={tasksNavLabel(title, badge)}
      // The collapsed sidebar shows no text: a dot on the icon instead.
      hasNotifications={tone !== undefined}
    >
      {tone && (
        <span className="waadoo-tasks-badge" data-tone={tone} aria-hidden>
          <span className="waadoo-tasks-badge__dot" />
          {count}
        </span>
      )}
    </SidebarItem>
  );
}
